//
// Copyright 2021 DXOS.org
//

import * as Runtime from 'effect/Runtime';
import * as EffectStream from 'effect/Stream';
import isEqualWith from 'lodash.isequalwith';

import {
  Event,
  MulticastObservable,
  Trigger,
  asyncTimeout,
  scheduleMicroTask,
  scheduleTask,
  scheduleTaskInterval,
  synchronized,
} from '@dxos/async';
import {
  type ClientServicesProvider,
  type ExportSpaceOptions,
  type Space,
  SPACE_TAG,
  type SpaceInternal,
  SpaceProperties,
} from '@dxos/client-protocol';
import { Context, cancelWithContext } from '@dxos/context';
import { type SpecificCredential, checkCredentialType } from '@dxos/credentials/assertions';
import {
  type CustomInspectable,
  type CustomInspectFunction,
  inspectCustom,
  loadashEqualityFn,
  todo,
  warnAfterTimeout,
} from '@dxos/debug';
import { Filter, Obj } from '@dxos/echo';
import { type DatabaseImpl, type EchoClient, type EchoDatabase, type SpaceSyncState } from '@dxos/echo-client';
import { isEdgePeerId } from '@dxos/echo-protocol';
import { EffectEx } from '@dxos/effect';
import { invariant } from '@dxos/invariant';
import { type PublicKey, type SpaceId } from '@dxos/keys';
import { log } from '@dxos/log';
import { decodeError, runServiceCall, subscribeStream } from '@dxos/protocols';
import {
  type Contact,
  CreateEpochRequest,
  Invitation,
  SpaceArchive,
  type Space as SpaceData,
  type SpaceMember,
  SpaceState,
  type UpdateMemberRoleRequest,
} from '@dxos/protocols/proto/dxos/client/services';
import { EdgeReplicationSetting } from '@dxos/protocols/proto/dxos/echo/metadata';
import { type SpaceSnapshot } from '@dxos/protocols/proto/dxos/echo/snapshot';
import {
  type Credential,
  type Epoch,
  SpaceMember as HaloSpaceMember,
  MembershipPolicy,
} from '@dxos/protocols/proto/dxos/halo/credentials';
import { type GossipMessage } from '@dxos/protocols/proto/dxos/mesh/teleport/gossip';
import { Timeframe } from '@dxos/timeframe';
import { trace } from '@dxos/tracing';

import { RPC_TIMEOUT } from '../common';
import { InvitationsProxy } from '../invitations';
import { createDeviceLocalBranchStore } from './branch-store';

const EPOCH_CREATION_TIMEOUT = 60_000;

/**
 * Returns the {@link Space} that owns the given object, or `undefined`.
 *
 * Use {@link Obj.getDatabase} when you only need DB/`spaceId` access; this
 * helper is retained only for callers that need {@link Space} proxy members
 * (`properties`, `members`, `key`, `state`, `listen`, identity).
 *
 * @deprecated Use {@link Obj.getSpace} instead.
 */
// TODO(burdon): Hypergraph.getSpace().
export const getSpace = (object?: any): Space | undefined => {
  if (!object) {
    return undefined;
  }

  const db = Obj.getDatabase(object);
  const id = db?.spaceId;
  if (id && '_getOwningObject' in db.graph) {
    const owner = (db.graph as { _getOwningObject: (id: SpaceId) => unknown })._getOwningObject(id);
    if (owner instanceof SpaceProxy) {
      return owner;
    }
  }

  return undefined;
};

export class SpaceProxy implements Space, CustomInspectable {
  readonly [SPACE_TAG] = true as const;

  private _ctx = Context.default();

  /**
   * Sent whenever any space data changes.
   */
  private readonly _anySpaceUpdate = new Event<SpaceData>();

  /**
   * @internal
   * To update the space query when a space changes.
   */
  // TODO(dmaretskyi): Make private.
  public readonly _stateUpdate = new Event<SpaceState>();

  private readonly _pipelineUpdate = new Event<SpaceData.PipelineState>();

  // TODO(dmaretskyi): Reconcile initialization states.

  /**
   * @internal
   * To unlock internal operations that should happen after the database is initialized but before initialize() completes.
   */
  public readonly _databaseInitialized = new Trigger();

  /**
   * @internal
   * Space proxy is fully initialized, database open, state is READY.
   */
  public readonly _initializationComplete = new Trigger();

  private _initializing = false;

  /**
   * @internal
   */
  _initialized = false;

  private readonly _db!: DatabaseImpl;
  private readonly _internal!: SpaceInternal;
  private readonly _invitationsProxy: InvitationsProxy;

  private readonly _state = MulticastObservable.from(this._stateUpdate, SpaceState.SPACE_CLOSED);
  private readonly _pipeline = MulticastObservable.from(this._pipelineUpdate, {});
  private readonly _membersUpdate = new Event<SpaceMember[]>();
  private readonly _members = MulticastObservable.from(this._membersUpdate, []);

  private _databaseOpen = false;
  private _error: Error | undefined = undefined;
  private _properties?: Obj.OfShape<SpaceProperties> = undefined;

  /**
   * When true, the space is waiting for the backend to finish re-initializing after a reconnect.
   * During this period, state updates that would regress from READY are ignored.
   * Once the backend reaches READY again, normal processing resumes.
   */
  private _awaitingBackendReady = false;

  constructor(
    private _clientServices: ClientServicesProvider,
    private _data: SpaceData,
    echoClient: EchoClient,
    private readonly _runtime: Runtime.Runtime<never> = Runtime.defaultRuntime,
  ) {
    log('construct', { key: _data.spaceKey, state: SpaceState[_data.state] });
    invariant(this._clientServices.services.InvitationsService, 'InvitationsService not available');
    this._invitationsProxy = new InvitationsProxy(
      this._clientServices.services.InvitationsService,
      this._clientServices.services.IdentityService,
      () => ({
        kind: Invitation.Kind.SPACE,
        spaceKey: this.key,
      }),
    );

    this._db = echoClient.constructDatabase({
      spaceId: this.id,
      spaceKey: this.key,
      owningObject: this,
      branchStore: createDeviceLocalBranchStore(this.id),
    });

    const self = this;
    this._internal = {
      get db() {
        return self._db;
      },
      get data() {
        return self._data;
      },
      createEpoch: this._createEpoch.bind(this),
      getCredentials: this._getCredentials.bind(this),
      getEpochs: this._getEpochs.bind(this),
      removeMember: this._removeMember.bind(this),
      migrate: this._migrate.bind(this),
      setEdgeReplicationPreference: this._setEdgeReplicationPreference.bind(this),
      syncToEdge: this._syncToEdge.bind(this),
      export: this._export.bind(this),
    };

    this._error = this._data.error ? decodeError(this._data.error) : undefined;

    // Update observables.
    this._stateUpdate.emit(this._currentState);
    this._pipelineUpdate.emit(_data.pipeline ?? {});
    this._membersUpdate.emit(_data.members ?? []);
  }

  toJSON() {
    return {
      id: this.id,
      db: this._db.toJSON(),
      state: SpaceState[this.state.get()],
    };
  }

  get id(): SpaceId {
    return this._data.id as SpaceId;
  }

  get key() {
    return this._data.spaceKey;
  }

  get tags(): string[] {
    return this._data.tags ?? [];
  }

  get membershipPolicy(): MembershipPolicy {
    return this._data.membershipPolicy ?? MembershipPolicy.INVITE;
  }

  get db(): EchoDatabase {
    return this._db;
  }

  get isOpen() {
    return this._data.state === SpaceState.SPACE_READY && this._initialized;
  }

  get properties(): Obj.OfShape<SpaceProperties> {
    this._throwIfNotInitialized();
    invariant(this._properties, 'Properties not available');
    return this._properties;
  }

  get state() {
    return this._state;
  }

  /**
   * @inheritdoc
   */
  get pipeline() {
    return this._pipeline;
  }

  /**
   * @inheritdoc
   */
  get invitations() {
    return this._invitationsProxy.created;
  }

  /**
   * @inheritdoc
   */
  get members() {
    return this._members;
  }

  /**
   * @inheritdoc
   */
  get internal(): SpaceInternal {
    return this._internal;
  }

  get error(): Error | undefined {
    return this._error;
  }

  get [Symbol.toStringTag](): string {
    return 'SpaceProxy';
  }

  [inspectCustom]: CustomInspectFunction = (depth, options, inspect) => {
    return `${options.stylize(this[Symbol.toStringTag], 'special')} ${inspect({
      id: this.id,
      state: SpaceState[this.state.get()],
    })}`;
  };

  /**
   * Current state of the space.
   * The database is ready to be used in `SpaceState.SPACE_READY` state.
   * Presence is available in `SpaceState.SPACE_CONTROL_ONLY` state.
   */
  private get _currentState(): SpaceState {
    if (this._data.state === SpaceState.SPACE_READY && !this._initialized) {
      return SpaceState.SPACE_INITIALIZING;
    } else {
      return this._data.state;
    }
  }

  /**
   * Called by SpaceList before open() to ensure this._ctx carries the parent trace context
   * before initialization has run.
   * @internal Package-private.
   */
  _setParentCtx(parentCtx: Context): void {
    if (!this._initialized && !this._initializing) {
      this._ctx = new Context({ parent: parentCtx });
    }
  }

  /**
   * Called by SpaceList to notify that a reconnection is starting.
   * The space will ignore state updates until the backend reaches READY again.
   * @internal Package-private.
   */
  _notifyReconnecting(): void {
    if (this._initialized && this._data.state === SpaceState.SPACE_READY) {
      this._awaitingBackendReady = true;
      log('entering reconnecting state', { key: this.key });
    }
  }

  /**
   * Called by EchoProxy to update this space instance.
   * Called once on initial creation.
   * @internal Package-private.
   */
  @synchronized
  async _processSpaceUpdate(ctx: Context, space: SpaceData): Promise<void> {
    // During reconnection, if we're waiting for the backend to re-initialize,
    // ignore all updates until it reaches READY (or REQUIRES_MIGRATION) again.
    if (this._awaitingBackendReady) {
      if (space.state === SpaceState.SPACE_READY || space.state === SpaceState.SPACE_REQUIRES_MIGRATION) {
        // Backend is ready again, resume normal processing.
        this._awaitingBackendReady = false;
        log('backend ready, resuming updates', { key: this.key, state: SpaceState[space.state] });
      } else {
        // Still waiting for backend to be ready, ignore this update.
        log('ignoring update while awaiting backend ready', { key: this.key, ignoredState: SpaceState[space.state] });
        return;
      }
    }

    const emitEvent = shouldUpdate(this._data, space);
    const emitPipelineEvent = shouldPipelineUpdate(this._data, space);
    const emitMembersEvent = shouldMembersUpdate(this._data.members, space.members);
    const isFirstTimeInitializing =
      space.state === SpaceState.SPACE_READY && !(this._initialized || this._initializing);
    const isReopening =
      this._data.state !== SpaceState.SPACE_READY && space.state === SpaceState.SPACE_READY && !this._databaseOpen;
    const shouldReset = this._databaseOpen && space.state === SpaceState.SPACE_REQUIRES_MIGRATION;

    log('update', {
      key: space.spaceKey,
      prevState: SpaceState[this._data.state],
      state: SpaceState[space.state],
      emitEvent,
      emitPipelineEvent,
      emitMembersEvent,
      isFirstTimeInitializing,
      isReopening,
      edgeReplication: space.edgeReplication,
    });

    this._data = space;

    if (isFirstTimeInitializing) {
      await this._initialize(ctx);
    } else if (isReopening) {
      this._ctx = new Context({ parent: ctx });
      await this._initializeDb(ctx);
    } else if (shouldReset) {
      await this._reset();
    }

    if (space.error) {
      this._error = decodeError(space.error);
    }

    if (this._initialized) {
      // Transition onto new automerge root.
      const automergeRoot = space.pipeline?.spaceRootUrl;
      if (automergeRoot) {
        log('set space root', { spaceKey: this.key, automergeRoot });
        // NOOP if the root is the same.
        await this._db.setSpaceRoot(automergeRoot);
      }
    }

    this._anySpaceUpdate.emit(space);
    if (emitEvent) {
      this._stateUpdate.emit(this._currentState);
    }
    if (emitPipelineEvent) {
      this._pipelineUpdate.emit(space.pipeline ?? {});
    }
    if (emitMembersEvent) {
      this._membersUpdate.emit(space.members!);
    }
  }

  private async _initialize(ctx: Context): Promise<void> {
    if (this._initializing || this._initialized) {
      return;
    }

    this._ctx = new Context({ parent: ctx });

    log('initializing...', { space: this.key });
    this._initializing = true;
    await this._invitationsProxy.open();
    await this._initializeDb(ctx);

    this._initialized = true;
    this._initializing = false;
    this._initializationComplete.wake();
    this._stateUpdate.emit(this._currentState);
    this._data.members && this._membersUpdate.emit(this._data.members);
    log('initialized', { space: this.key });
  }

  @trace.span({ showInBrowserTimeline: true })
  private async _initializeDb(ctx: Context): Promise<void> {
    this._databaseOpen = true;

    {
      const automergeRoot = this._data.pipeline?.spaceRootUrl;
      if (automergeRoot !== undefined) {
        await this._db.setSpaceRoot(automergeRoot);
      } else {
        log.warn('no automerge root found for space', { spaceId: this.id });
      }
      await this._db.open(ctx);
    }

    log('ready');

    this._databaseInitialized.wake();

    const propertiesAvailable = new Trigger();
    // Set properties document when it's available.
    // NOTE: Emits state update event when properties are first available.
    //   This is needed to ensure reactivity for newly created spaces.
    // TODO(wittjosiah): Transfer subscriptions from cached properties to the new properties object.
    {
      const unsubscribe = this._db.query(Filter.type(SpaceProperties)).subscribe(
        (query) => {
          if (query.results.length === 1) {
            this._properties = query.results[0];
            propertiesAvailable.wake();
            this._stateUpdate.emit(this._currentState);
            scheduleMicroTask(this._ctx, () => {
              unsubscribe();
            });
          }
        },
        { fire: true },
      );
    }
    await warnAfterTimeout(5_000, 'Finding properties for a space', () =>
      cancelWithContext(this._ctx, propertiesAvailable.wait()),
    );
  }

  /**
   * Called by EchoProxy close.
   * @internal Package-private.
   */
  @synchronized
  async _destroy(): Promise<void> {
    await this._reset();
  }

  private async _reset(): Promise<void> {
    log('destroying...');
    await this._ctx.dispose();
    this._ctx = Context.default();
    await this._invitationsProxy.close();
    await this._db.close();
    this._initializationComplete.reset();
    this._databaseInitialized.reset();
    this._initializing = false;
    this._initialized = false;
    this._databaseOpen = false;
    log('destroyed');
  }

  async open(): Promise<void> {
    await this._openInternal(this._ctx);
  }

  @trace.span({ showInBrowserTimeline: true, op: 'lifecycle' })
  private async _openInternal(ctx: Context): Promise<void> {
    await runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.updateSpace({ spaceKey: this.key, state: SpaceState.SPACE_ACTIVE }),
      { timeout: RPC_TIMEOUT, label: 'SpacesService.updateSpace' },
    );
  }

  async close(): Promise<void> {
    await this._closeInternal(this._ctx);
  }

  @trace.span({ showInBrowserTimeline: true, op: 'lifecycle' })
  private async _closeInternal(ctx: Context): Promise<void> {
    if (this._databaseOpen) {
      await this._db.flush();
    }
    await runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.updateSpace({ spaceKey: this.key, state: SpaceState.SPACE_INACTIVE }),
      { timeout: RPC_TIMEOUT, label: 'SpacesService.updateSpace' },
    );
  }

  async delete(): Promise<void> {
    await this._deleteInternal(this._ctx);
  }

  @trace.span({ showInBrowserTimeline: true, op: 'lifecycle' })
  private async _deleteInternal(ctx: Context): Promise<void> {
    if (this._databaseOpen) {
      await this._db.flush();
    }
    await runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.updateSpace({ spaceKey: this.key, state: SpaceState.SPACE_DELETED }),
      { timeout: RPC_TIMEOUT, label: 'SpacesService.updateSpace' },
    );
  }

  /**
   * Waits until the space is in the ready state, with database initialized.
   */
  async waitUntilReady(): Promise<this> {
    await cancelWithContext(this._ctx, this._initializationComplete.wait());
    return this;
  }

  /**
   * Post a message to the space.
   */
  async postMessage(channel: string, message: any): Promise<void> {
    await runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.postMessage({
        spaceKey: this.key,
        channel,
        message: {
          ...message,
          '@type': message['@type'] || 'google.protobuf.Struct',
        },
      }),
      { timeout: RPC_TIMEOUT, label: 'SpacesService.postMessage' },
    );
  }

  /**
   * Listen for messages posted to the space.
   */
  listen(channel: string, callback: (message: GossipMessage) => void): () => Promise<void> {
    const cleanup = subscribeStream(
      this._runtime,
      this._clientServices.rpc.SpacesService.subscribeMessages({ spaceKey: this.key, channel }),
      { onData: callback },
    );
    return async () => cleanup();
  }

  /**
   * Creates a delegated or interactive invitation.
   */
  share(options?: Partial<Invitation>) {
    this._throwIfNotInitialized();
    log('create invitation', options);
    return this._invitationsProxy.share({ ...options, spaceKey: this.key });
  }

  async admitContact(contact: Contact): Promise<void> {
    await runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.admitContact({
        spaceKey: this.key,
        role: HaloSpaceMember.Role.ADMIN,
        contact,
      }),
      { label: 'SpacesService.admitContact' },
    );
  }

  /**
   * Requests member role update.
   */
  updateMemberRole(request: Omit<UpdateMemberRoleRequest, 'spaceKey'>): Promise<void> {
    this._throwIfNotInitialized();
    return runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.updateMemberRole({
        spaceKey: this.key,
        memberKey: request.memberKey,
        newRole: request.newRole,
      }),
      { label: 'SpacesService.updateMemberRole' },
    );
  }

  /**
   * Implementation method.
   */
  createSnapshot(): Promise<SpaceSnapshot> {
    return todo();
    // return this._serviceProvider.services.SpaceService.createSnapshot({ space_key: this.key });
  }

  private async _removeMember(memberKey: PublicKey): Promise<void> {
    return runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.updateMemberRole({
        spaceKey: this.key,
        memberKey,
        newRole: HaloSpaceMember.Role.REMOVED,
      }),
      { label: 'SpacesService.updateMemberRole' },
    );
  }

  private async _createEpoch({
    migration,
    automergeRootUrl,
  }: {
    migration?: CreateEpochRequest.Migration;
    automergeRootUrl?: string;
  } = {}): Promise<void> {
    await this._createEpochInternal(this._ctx, { migration, automergeRootUrl });
  }

  @trace.span({ showInBrowserTimeline: true, op: 'lifecycle' })
  private async _createEpochInternal(
    ctx: Context,
    {
      migration,
      automergeRootUrl,
    }: {
      migration?: CreateEpochRequest.Migration;
      automergeRootUrl?: string;
    } = {},
  ): Promise<void> {
    log('create epoch', { migration, automergeRootUrl });
    const { controlTimeframe: targetTimeframe } = await runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.createEpoch({
        spaceKey: this.key,
        migration,
        automergeRootUrl,
      }),
      { timeout: EPOCH_CREATION_TIMEOUT, label: 'SpacesService.createEpoch' },
    );

    if (targetTimeframe) {
      await warnAfterTimeout(5_000, 'Waiting for the created epoch to be applied', () =>
        this._anySpaceUpdate.waitForCondition(() => {
          const currentTimeframe = this._data.pipeline?.currentControlTimeframe;
          return (currentTimeframe && Timeframe.dependencies(targetTimeframe, currentTimeframe).isEmpty()) ?? false;
        }),
      );
    }
  }

  private async _getCredentials(): Promise<Credential[]> {
    const credentials = await EffectEx.runInRuntime(this._runtime)(
      this._clientServices.rpc.SpacesService.queryCredentials({ spaceKey: this.key, noTail: true }).pipe(
        EffectStream.runCollect,
      ),
    );
    return [...credentials];
  }

  private async _getEpochs(): Promise<SpecificCredential<Epoch>[]> {
    const credentials = await this._getCredentials();
    return credentials.filter((credential) => checkCredentialType(credential, 'dxos.halo.credentials.Epoch'));
  }

  private async _migrate(): Promise<void> {
    await this._createEpoch({
      migration: CreateEpochRequest.Migration.MIGRATE_REFERENCES_TO_DXN,
    });

    // Needed to have space root set to be able to make next check.
    await this._databaseInitialized.wait();

    if (this._db.getNumberOfInlineObjects() > 1) {
      await this._createEpoch({
        migration: CreateEpochRequest.Migration.FRAGMENT_AUTOMERGE_ROOT,
      });
    }
  }

  private async _setEdgeReplicationPreference(setting: EdgeReplicationSetting): Promise<void> {
    await runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.updateSpace({
        spaceKey: this.key,
        edgeReplication: setting,
      }),
      { timeout: RPC_TIMEOUT, label: 'SpacesService.updateSpace' },
    );
    // TODO(dmaretskyi): Might cause a race-condition if the property is updated multiple times.
    await asyncTimeout(
      this._anySpaceUpdate.waitForCondition(() => {
        return this._data.edgeReplication === setting;
      }),
      2_000,
      'Waiting for the edge replication to be enabled',
    );
  }

  private async _syncToEdge(opts?: {
    timeout?: number;
    onProgress: (state: SpaceSyncState.PeerState | undefined) => void;
  }): Promise<void> {
    await using ctx = new Context({ parent: this._ctx });

    if (this._data.edgeReplication !== EdgeReplicationSetting.ENABLED) {
      throw new Error('Edge replication is disabled');
    }

    // Drain pending head updates before sampling sync state. `db.flush()` calls
    // `host.flush`, which (a) flushes loaded docs to disk and (b) `runBlocking`s
    // `_onHeadsChangedTask` so any `_afterSave`-recorded heads have propagated into
    // `_collectionSynchronizer`. Without this, `getCollectionSyncState` is eventually
    // consistent and a stream emission may report `{0, 0, 0}` from stale local heads,
    // causing the latch below to resolve prematurely.
    await this._db.flush();

    return await new Promise<void>((resolve, reject) => {
      scheduleTask(
        ctx,
        () => {
          reject(new Error('Timeout syncing to EDGE'));
        },
        opts?.timeout ?? 60_000,
      );

      const checkSyncState = (syncState: SpaceSyncState) => {
        const edgePeer = syncState.peers?.find((state) => isEdgePeerId(state.peerId, this.id));
        if (opts?.onProgress) {
          opts.onProgress(edgePeer);
        }

        if (
          edgePeer &&
          edgePeer.missingOnRemote === 0 &&
          edgePeer.missingOnLocal === 0 &&
          edgePeer.differentDocuments === 0
        ) {
          resolve();
        }
      };

      ctx.onDispose(this._db.subscribeToAutomergeSyncState(ctx, checkSyncState));
      // TODO(dmaretskyi): Still need polling, otherwise this gets stuck.
      scheduleTaskInterval(
        ctx,
        async () => {
          checkSyncState(await this._db.getAutomergeSyncState());
        },
        1_000,
      );

      scheduleMicroTask(ctx, async () => {
        checkSyncState(await this._db.getAutomergeSyncState());
      });
    });
  }

  private _throwIfNotInitialized(): void {
    if (!this._initialized) {
      throw new Error('Space is not initialized.');
    }
  }

  private async _export(options?: ExportSpaceOptions): Promise<SpaceArchive> {
    await this._db.flush();
    const { archive } = await runServiceCall(
      this._runtime,
      this._clientServices.rpc.SpacesService.exportSpace({
        spaceId: this.id,
        format: options?.format ?? SpaceArchive.Format.BINARY,
      }),
      { label: 'SpacesService.exportSpace' },
    );
    return archive;
  }
}

const shouldUpdate = (prev: SpaceData, next: SpaceData) => {
  return prev.state !== next.state;
};

const shouldPipelineUpdate = (prev: SpaceData, next: SpaceData) => {
  return !isEqualWith(prev.pipeline, next.pipeline, loadashEqualityFn);
};

const shouldMembersUpdate = (prev: SpaceMember[] | undefined, next: SpaceMember[] | undefined) => {
  if (!next) {
    return false;
  }

  return !isEqualWith(prev, next, loadashEqualityFn);
};
